import { FormStatus, AccessRole } from 'types';
import DocList, { ViewMode } from 'components/list/docList/docList';
import TableList from 'components/list/tableList/tableList';
import TableListRow from 'components/list/tableList/tableListRow';
import TableListCell from 'components/list/tableList/tableListCell';
import TransactionFilter from './TransactionFilter';
import useTransactions from '@/hooks/useTransactions';
import useStorages from '@/hooks/useStorages';
import { useTranslation } from 'react-i18next';
import DateTime from '@/components/text/DateTime';
import Tag from '../tags/tag';
import LoadMore from '../list/loadMore';
import Section from '../layout/section';
import IconImage from '../image/IconImage';
import { ReactComponent as DescriptionIcon } from 'assets/icons/description.svg';
import { Link } from 'react-router-dom';
import FloatingButtonGroupButton from '../buttons/FloatingButtonGroupButton';
import FloatingButtonGroup from '../buttons/floatingButtonGroup';
import { ReactComponent as AddIcon } from 'assets/icons/add.svg';
import Button from '../inputs/button';
import { ReactComponent as ArrowDropDownIcon } from 'assets/icons/arrow_drop_down.svg';
import useAccess from '@/hooks/useAccess';

interface TransactionListProps {
  storageId?: string;
  displayHelper?: boolean;
}

const TransactionList: React.FC<TransactionListProps> = ({ storageId, displayHelper = true }) => {

  const { t } = useTranslation();

  const { storage, isCustomersAndSuppliersEnabled } = useStorages({ storageId });
  const showPartner = isCustomersAndSuppliersEnabled();

  const {
    transactions,
    filteredTransactions,
    filterQuery,
    setFilterQuery,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    typeFilter,
    setTypeFilter,
    transactionsStatus,
    hasNextPage,
    getMore
  } = useTransactions({ storageId })

  const { limitToRoles } = useAccess({ storageId });

  if (
    (storage?.counters?.transactionCounter === undefined || storage?.counters?.transactionCounter === 0) &&
    displayHelper &&
    limitToRoles([AccessRole.EDITOR, AccessRole.OWNER, AccessRole.WORKER])
  ) {
    return (
        <Section design={`card-centered`}>
            <IconImage>
                <DescriptionIcon />
            </IconImage>
            <h2>{t(`titleNoTransactions`)}</h2>
            <p>{t('transactionListHelperText')}</p>
            <FloatingButtonGroup id={`transaction-list-helper`} openingButton={<Button wideOnMobile design="primary"><AddIcon />{t('buttonNewTransaction')}<ArrowDropDownIcon /></Button>}>
                <FloatingButtonGroupButton to={`/storage/transactions/create/incoming`}>{t('buttonNewIncoming')}</FloatingButtonGroupButton>
                <FloatingButtonGroupButton to={`/storage/transactions/create/outgoing`}>{t('buttonNewOutgoing')}</FloatingButtonGroupButton>
            </FloatingButtonGroup>
        </Section>
    )
  }

  return (
    <>
      <DocList
        id="transaction_list"
        viewModes={[ViewMode.Table]}
        defaultViewMode={ViewMode.Table}
        filterElement={
          <TransactionFilter
            filterQuery={filterQuery}
            setFilterQuery={setFilterQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
          />
        }
        showResults={filteredTransactions.length > 0 || transactionsStatus !== FormStatus.LOADING}
      >
        <div data-view-mode={ViewMode.Table}>
          <TableList>
            <TableListRow isHeader={true}>
              <TableListCell>{t('labelId')}</TableListCell>
              <TableListCell>{t('labelType')}</TableListCell>
              <TableListCell>{t('labelStatus')}</TableListCell>
              <TableListCell>{t('labelCreatedAt')}</TableListCell>
              {showPartner && <TableListCell>{t('labelCustomerSupplier', 'Partner')}</TableListCell>}
            </TableListRow>
            {filteredTransactions.map((transaction) => {
              const contactName = showPartner
                ? transaction.customer?.customerName || transaction.supplier?.supplierName || '-'
                : null;
              return (
                <TableListRow
                  key={transaction.transactionId}
                  to={`/storage/transactions/${transaction.id}`}
                >
                  <TableListCell>{transaction.transactionId}</TableListCell>
                  <TableListCell className='capitalize'>{
                    transaction.type === 'incoming' ? (
                      <Tag colorScheme='green'>
                        <span className='hidden sm:inline'>{t('labelIncoming')}</span>
                        <span className='sm:hidden'>{t('labelIncomingShort')}</span>
                      </Tag>
                    ) : (
                      <Tag colorScheme='red'>
                        <span className='hidden sm:inline'>{t('labelOutgoing')}</span>
                        <span className='sm:hidden'>{t('labelOutgoingShort')}</span>
                      </Tag>
                    )
                  }</TableListCell>
                  <TableListCell>{
                    transaction.isDraft === true && transaction.isFinalized === false
                      ? <Tag colorScheme='yellow'>{t('labelDraft', 'Draft')}</Tag>
                      : <Tag colorScheme='blue'>{t('labelFinalized', 'Published')}</Tag>
                  }</TableListCell>
                  <TableListCell>
                    <DateTime timestamp={transaction.keyHistory?.createdAt} hideTimeOnSm hideTimeOnMd />
                  </TableListCell>
                  {showPartner && <TableListCell>{contactName}</TableListCell>}
                </TableListRow>
              );
            })}
            {transactionsStatus === FormStatus.LOADING && Array.from({ length: 3 }).map((_, index) => (
              <TableListRow key={index}>
                <TableListCell><p className='skeleton'></p></TableListCell>
                <TableListCell><p className='skeleton'></p></TableListCell>
                <TableListCell><p className='skeleton'></p></TableListCell>
                <TableListCell><p className='skeleton'></p></TableListCell>
                {showPartner && <TableListCell><p className='skeleton'></p></TableListCell>}
              </TableListRow>
            ))}
          </TableList>
        </div>
      </DocList>
      <LoadMore
        hasNextPage={hasNextPage}
        action={getMore}
        isLoading={transactionsStatus === FormStatus.LOADING}
      />
    </>
  );
};

export default TransactionList;
